'use client';

import { useSystemLabels } from '@eliadmin/core/src/hooks/use-system-labels';
import Link from '@eliadmin/core/src/components/link';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useCartQueryOpts } from '@eliadmin/core/src/hooks/cart/use-cart';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useQuery } from '@tanstack/react-query';
import { MdOutlineShoppingCart } from 'react-icons/md';

/**
 * Figma header cart: 20px cart glyph with a cream count badge, followed by the cart total rather
 * than the word "Košík".
 */
export function HeaderCartButton({ whiteText }: { whiteText: boolean }) {
  const systemLabels = useSystemLabels();
  const { data: cart } = useQuery({ ...useCartQueryOpts() });
  const generalLayout = useGlobalStore(s => s.generalLayout);

  const total = formatPrice(
    cart?.total ?? 0,
    generalLayout?.currency,
    generalLayout?.currency_settings,
  );

  return (
    <Button asChild variant={'none'} size={'none'} className="gap-2">
      <Link aria-label={`${systemLabels('cart')} ${total}`} href={ROUTES.CART()}>
        {
          <>
            <div className="relative">
              <CartBadge amount={cart?.items?.length ?? 0} />
              <MdOutlineShoppingCart
                size={20}
                className={cn(whiteText ? 'text-white' : 'text-black')}
              />
            </div>
            <span
              className={cn(
                'whitespace-nowrap text-sm leading-[1.3]',
                whiteText ? 'text-white' : 'text-black',
              )}
            >
              {total}
            </span>
          </>
        }
      </Link>
    </Button>
  );
}

function CartBadge({ amount }: { amount: number }) {
  if (amount <= 0) return null;

  return (
    <span className="absolute -right-2 -top-2 flex size-4 items-center justify-center rounded-full bg-[#FAE9CE] text-[10px] font-normal leading-[1.3] text-[#B3631E]">
      {amount}
    </span>
  );
}
